import { Inject, Injectable } from '@angular/core';
import {
    GenericApiServiceFactory,
    GenericBoundedApiService
} from '@lsq-frontend/angular-shared/api-service';
import { SingletonServiceGuard } from '@lsq-frontend/angular-shared/features/generic-get-services';
import { ISettings, SETTINGS } from '@lsq-frontend/angular-shared/settings';
import { FastTrackApiRoutes } from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    AcknowledgeNotificationStateRequest,
    ListNotificationsRequest,
    ListNotificationsResponse,
    Notification
} from '@lsq-frontend/remote-services/enterprise/common';
import {
    BehaviorSubject,
    combineLatest,
    from,
    interval,
    merge,
    Observable,
    of,
    Subject
} from 'rxjs';
import { catchError, exhaustMap, filter, map, shareReplay } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class NotificationsService {
    private _notificationPollingTimer = interval(
        this._settings.notificationPollingTimerInSeconds * 1000
    );
    private _refreshSubject = new Subject<undefined>();
    private _isExpandedSubject = new BehaviorSubject<boolean>(false);
    private _listNotificationsService: GenericBoundedApiService<ListNotificationsRequest>;
    private _acknowledgeNotificationsStateService: GenericBoundedApiService;

    public isExpanded$ = this._isExpandedSubject.asObservable();
    public notifications$: Observable<Notification[]>;
    public notificationCount$: Observable<number>;

    private _allowNotificationFetchingSubject = new BehaviorSubject<boolean>(false);
    public allowNotificationFetching$ = this._allowNotificationFetchingSubject.asObservable();

    constructor(
        @Inject(SETTINGS) private _settings: ISettings,
        private _genericApiServiceFactory: GenericApiServiceFactory
    ) {
        SingletonServiceGuard.register('NotificationsService');
        this._listNotificationsService = this._genericApiServiceFactory.createBounded(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.ListNotifications}`
        );
        this._acknowledgeNotificationsStateService = this._genericApiServiceFactory.createBounded(
            `/${FastTrackApiRoutes.Api}/${FastTrackApiRoutes.BatchAcknowledgeNotification}`
        );

        this.notifications$ = combineLatest(
            merge(this._notificationPollingTimer, this._refreshSubject),
            this.allowNotificationFetching$
        ).pipe(
            filter(([_, isFetching]) => isFetching),
            exhaustMap(() =>
                from(this.fetchNotifications()).pipe(
                    // If we run into an error, just keep going with an empty array.
                    catchError(() => of([]))
                )
            ),
            shareReplay(1)
        );
        this.notificationCount$ = this.notifications$.pipe(
            map((notifications) => notifications.length)
        );
    }

    public refreshNotifications(): void {
        this._refreshSubject.next();
    }

    public fetchNotifications(): Promise<Notification[]> {
        return this._listNotificationsService
            .executeOnce(new ListNotificationsRequest())
            .then((response: ListNotificationsResponse) => response.rows);
    }

    public async acknowledgeNotifications(notifications: Notification[]): Promise<void> {
        await this._acknowledgeNotificationsStateService.executeOnce(
            new AcknowledgeNotificationStateRequest({
                notificationKeys: notifications.map((notification) => notification.notificationKey)
            })
        );
        this.refreshNotifications();
    }

    public toggleNotificationFetching(turnOn: boolean): void {
        this._allowNotificationFetchingSubject.next(turnOn);
    }

    public toggleExpanded(): void {
        this._isExpandedSubject.next(!this._isExpandedSubject.getValue());
    }

    public show(): void {
        this._isExpandedSubject.next(true);
    }

    public hide(): void {
        this._isExpandedSubject.next(false);
    }
}
